import type { Dayjs } from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';

export enum GiftCardValidityType {
  Preset = 'Preset',
  Custom = 'Custom',
}

export enum GiftCardValidityPeriod {
  Year = 'Year',
  HalfYear = 'HalfYear',
}

export interface GiftCardValidityCreateArgs {
  type: GiftCardValidityType,
  period: GiftCardValidityPeriod | null,
  expirationDate: Dayjs | null,
}

export class GiftCardValidity extends DataModel {
  static readonly TYPE_ID: string = 'b45f0e692c764b34a995c8006400b841';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly type: GiftCardValidityType,
    public readonly period: GiftCardValidityPeriod | null,
    public readonly expirationDate: Dayjs | null,
  ) {
    super();
  }

  get $typeId(): string {
    return GiftCardValidity.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(GiftCardValidity.SCHEMA_VERSION);
  }

  static create(args: GiftCardValidityCreateArgs): GiftCardValidity {
    return new GiftCardValidity(
        args.type,
        args.period,
        TimeZone.cloneDateOrNull(args.expirationDate),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GiftCardValidity {
    return this.create({
      type: toEnumFromString(MapUtils.getString(map, 'type'), GiftCardValidityType),
      period: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'period'), GiftCardValidityPeriod),
      expirationDate: context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'expirationDate')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'type': fromEnumAsString(this.type),
      'period': fromEnumAsStringOrNull(this.period),
      'expirationDate': context.encodeDateOrNull(this.expirationDate),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith(data: Partial<GiftCardValidity> = {}): this {
    return GiftCardValidity.create({ ...this, ...data }) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.type === this.type &&
      other.period === this.period &&
      TimeZone.isSameInstantOrNull(other.expirationDate, this.expirationDate);
  }
}

export class GiftCardValidityBuilder extends DataModelBuilder<GiftCardValidity> {
  build(context: SerializationContext, map: SerializedData): GiftCardValidity {
    return GiftCardValidity.fromMap(context, map);
  }
}
